import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Field, Toast, TextArea, Dropdown, Loader } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PopupContainer
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import { LinkPopup } from '@applicationKYC/LegalEntityQuestionnaire/components/linkPopup/linkPopup';
import activityChoices from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/farmSection/pages/farmActivityPage/activityChoices.json';
import activityChoicesV21 from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/farmSection/pages/farmActivityPage/activityChoicesV21.json';
import { history } from '@config';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { selectKycAppState } from '@redux';

const defaultValues = {
  description: null,
  naceCode: null
};

const linkPopupGroup = {
  text: 'link_popup_activity_description_title',
  popupKey: 'link_popup_activity'
};

const linkPopupGroupNace = {
  description: 'nace_code_v21_info_message',
  text: 'nace_code_more_info_button_text',
  popupKey: 'link_popup_nace_codes'
};

const config = {
  farmerVersion: {
    legacy: '2'
  }
};

export const FarmActivityPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const activityChoicesToUse =
    farmerInformation?.version > config.farmerVersion.legacy ? activityChoicesV21 : activityChoices;

  const validationSchema = useMemo(
    () =>
      Yup.object({
        description: Yup.string().required(t('validation_required_field')).nullable(),
        naceCode: Yup.string().required(t('validation_required_field')).nullable()
      }),
    [t]
  );

  const initialValues = { ...defaultValues, ...farmerInformation?.farmSection?.farmActivityPage };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        await onSubmit?.(data, 'farmActivityPage');
        history.push('/FarmersQuestionnaire?success=farmSection');
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader noSubHeader>
        <PageHeader>{t('farmers_farm_activity_header')}</PageHeader>
      </FarmersQuestionnaireHeader>
      <PopupContainer>
        <LinkPopup key={JSON.stringify(linkPopupGroup)} group={linkPopupGroup} farmers />
      </PopupContainer>
      <Toast
        isVisible
        animate={false}
        variant="info"
        header={t('farmers_farm_activity_informational_header')}
      />
      {farmerInformation?.version > config.farmerVersion.legacy && (
        <PopupContainer>
          <LinkPopup key={JSON.stringify(linkPopupGroupNace)} group={linkPopupGroupNace} farmers />
        </PopupContainer>
      )}
      <Field
        Component={Dropdown}
        placeholder={t('farmers_farm_activity_nace')}
        options={activityChoicesToUse.map((option) => ({
          ...option,
          label: t(option.label)
        }))}
        value={values.naceCode}
        name="naceCode"
        validationHelpers={validationHelpers}
        validateOnChange
        validateOnBlur
        required
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={TextArea}
        name="description"
        value={values.description}
        placeholder={t('farmers_farm_activity_description')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        autoHeight
        maxHeight={288}
        validateOnBlur
        maxLength={500}
        counter
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
